import { useGSAP } from "@gsap/react";
import { useRef, useState } from "react";

import { gsap } from "gsap";
import { FilterData } from "../../../sanity/lib/queries/getSolutionFilterData";

export const SolutionFilter = ({
    onFilterSolutions,
    setIsListLayout,
    filterValues,
    isListLayout,
    label,
    industryLabel,
    serviceLabel,
    filterData,
}: {
    onFilterSolutions: (id: string) => void;
    setIsListLayout: (isListLayout: boolean) => void;
    filterValues: Array<string>;
    isListLayout: boolean;
    label: string;
    industryLabel: string;
    serviceLabel: string;
    filterData: FilterData;
}) => {
    const tl = useRef<gsap.core.Timeline | null>(null);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    useGSAP(() => {
        tl.current = gsap.timeline({ paused: true });

        tl.current
            .addLabel("start")
            .fromTo(
                ".filter-panel",
                { height: 0 },
                {
                    height: "auto",
                    duration: 0.5,
                    ease: "power4.inOut",
                },
            )
            .fromTo(
                ".filter-count",
                { width: 0 },
                {
                    width: "100%",
                    duration: 0.5,
                    ease: "power4.inOut",
                },
                "start",
            )
            .to(
                ".filter-icon path",
                {
                    stroke: "white",
                    duration: 0.3,
                    ease: "power4.inOut",
                },
                "start",
            )
            .to(
                ".filter-icon",
                {
                    rotate: "45deg",
                    duration: 0.3,
                    ease: "power4.inOut",
                },
                "start",
            )
            .to(
                ".filter-icon-bg",
                {
                    backgroundColor: "black",
                    duration: 0.3,
                    ease: "power4.inOut",
                },
                "start",
            );

        return () => {
            tl.current?.kill();
        };
    }, []);

    const toggleFilter = () => {
        if (tl.current?.isActive()) return;

        if (isFilterOpen) {
            tl.current?.reverse().then(() => {
                setIsFilterOpen(false);
            });
        } else {
            tl.current?.play().then(() => {
                setIsFilterOpen(true);
            });
        }
    };

    return (
        <div className="pt-5 pb-5 md:pb-5 filter">
            <div className="flex justify-between">
                <button
                    onClick={toggleFilter}
                    className="flex items-center gap-4 md:mb-8 m-title md:d-headline-4 filter-button"
                >
                    <span className="flex items-center lowercase">
                        {label}
                        {filterValues.length > 0 && (
                            <span className="overflow-hidden filter-count">
                                {" (0"}
                                {filterValues.length}
                                {")"}
                            </span>
                        )}
                    </span>
                    <span className="relative">
                        <span className="absolute w-12 h-12 bg-white rounded-full -z-10 filter-icon-bg top-1/2 left-1/2 -translate-1/2" />

                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="block filter-icon"
                        >
                            <g>
                                <path d="M4 11.999L20 11.999" stroke="black" strokeWidth="1.5" />
                                <path d="M12 19.999V3.99902" stroke="black" strokeWidth="1.5" />
                            </g>
                        </svg>
                    </span>
                </button>

                <div className="relative flex items-start gap-4 md:hidden">
                    <button
                        className="relative"
                        onClick={() => setIsListLayout(true)}
                        aria-label="Zur Listenansicht wechseln"
                        aria-pressed={isListLayout}
                    >
                        <span
                            aria-hidden="true"
                            className={`-z-10 absolute w-10 h-10 bg-black rounded-full top-1/2 left-1/2 -translate-1/2 ${!isListLayout ? "opacity-0" : "opacity-100"}`}
                        ></span>
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                            focusable="false"
                        >
                            <rect
                                x="8"
                                y="6.5"
                                width="8"
                                height="11"
                                stroke={`${!isListLayout ? "black" : "white"}`}
                            />
                        </svg>
                    </button>
                    <button
                        className="relative"
                        onClick={() => setIsListLayout(false)}
                        aria-label="Zur Gridansicht wechseln"
                        aria-pressed={!isListLayout}
                    >
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                            focusable="false"
                        >
                            <g>
                                <rect
                                    x="2.5"
                                    y="6.5"
                                    width="8"
                                    height="11"
                                    stroke={`${!isListLayout ? "white" : "black"}`}
                                />
                                <rect
                                    x="13.5"
                                    y="6.5"
                                    width="8"
                                    height="11"
                                    stroke={`${!isListLayout ? "white" : "black"}`}
                                />
                            </g>
                        </svg>

                        <span
                            aria-hidden="true"
                            className={`-z-10 absolute w-10 h-10 bg-black rounded-full top-1/2 left-1/2 -translate-1/2 ${isListLayout ? "opacity-0" : "opacity-100"}`}
                        ></span>
                    </button>
                </div>
            </div>

            <div className="grid h-0 grid-cols-2 gap-10 overflow-hidden md:gap-0 filter-panel">
                <div className="col-span-2 md:col-span-1">
                    <p className="m-body-m md:d-body-m pb-2.5 pt-7.5 md:pt-0">{industryLabel}</p>
                    <div>
                        {filterData.industries?.map((industry) => (
                            <button
                                onClick={() => {
                                    onFilterSolutions(industry._id);
                                }}
                                key={industry._id}
                                className="m-body-l md:d-body-l py-2 md:py-2.5 flex items-center gap-2"
                            >
                                {industry.title}
                                {filterValues.indexOf(industry._id) !== -1 && (
                                    <span className="block w-4 h-4 bg-black rounded-full"></span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="col-span-2 pb-5 md:col-span-1 md:pb-0">
                    <p className="d-body-m pb-2.5">{serviceLabel}</p>
                    <div>
                        {filterData.services?.map((service) => (
                            <button
                                onClick={() => {
                                    onFilterSolutions(service._id);
                                }}
                                key={service._id}
                                className="m-body-l md:d-body-l py-2 md:py-2.5 flex items-center gap-2"
                            >
                                {service.title}
                                {filterValues.indexOf(service._id) !== -1 && (
                                    <span className="block w-4 h-4 bg-black rounded-full"></span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
